import type {ReactElement} from 'react';
import {useRef} from 'react';
import {PreviewLoadingContent} from '@Components/social-media/components/preview-loading-content/preview-loading-content';
import {SocialPostPreviewSkeleton} from '@Components/social-media/components/social-post-preview-skeleton/social-post-preview-skeleton';
import {SocialPostContent} from '@Components/social-media/components/social-post-content-multiposting';
import {SocialPostContentType} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {getMobilePreviewWindowHeight} from '@Components/social-media-wizard/social-media-wizard-library';
import type {WizardPreviewPanelProps} from './wizard-preview-panel.types';
import {PREVIEW_SURFACE_WIDTH_INSET_PX} from './wizard-preview-panel.types';
import styles from '../../social-media-wizard.module.scss';

export function WizardPreviewPanel({
  wizardStep,
  designsToPublishTo,
  postPublishingParams,
  designsGraphicsMetaMap,
  isSelectTemplateFlow,
  isMobileView,
  isPreviewLoading,
  areAllPreviewsUpToDate,
  ctaSlot,
  onDesignPreviewClick,
  onPreviewLoaded,
  containerClasses,
  previewWidthInsetPx,
}: WizardPreviewPanelProps): ReactElement {
  const previewParentRef = useRef<HTMLDivElement>(null);

  const getOrphanedPreviewContainerClickHandler = (): VoidFunction | undefined => {
    if (!onDesignPreviewClick) {
      return undefined;
    }

    return (): void => {
      if (isSelectTemplateFlow || isPreviewLoading) {
        return;
      }

      onDesignPreviewClick();
    };
  };

  const onLoadCallback = (): void => {
    onPreviewLoaded();
  };

  const getPreviewStyles = (): NonNullable<unknown> | undefined => {
    if (!isMobileView) {
      return undefined;
    }

    return {
      height: `${getMobilePreviewWindowHeight()}px`,
    };
  };

  const getSocialPostContent = (): ReactElement | null => {
    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS && !areAllPreviewsUpToDate) {
      return null;
    }

    return (
      <SocialPostContent
        designsToPublish={designsToPublishTo}
        onLoadCallback={onLoadCallback}
        areDetailsLoading={false}
        parentContainerRef={previewParentRef}
        type={SocialPostContentType.ORPHANED_PREVIEW}
        hasSkeletonLoadingState
        onOrphanedPreviewContainerClick={getOrphanedPreviewContainerClickHandler()}
        accountsToPublishTo={[]}
        publishingParams={postPublishingParams}
        designsGraphicMetaMap={designsGraphicsMetaMap}
        previewWidthInsetPx={previewWidthInsetPx ?? PREVIEW_SURFACE_WIDTH_INSET_PX}
      />
    );
  };

  const isLoading = wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS ? isPreviewLoading && !areAllPreviewsUpToDate : isPreviewLoading;

  return (
    <div ref={previewParentRef} style={getPreviewStyles()} className={containerClasses ?? styles.previewWrapper}>
      <PreviewLoadingContent isLoading={isLoading} loadingPlaceholder={<SocialPostPreviewSkeleton />} contentClasses="_full-height">
        {getSocialPostContent()}
      </PreviewLoadingContent>
      {ctaSlot}
    </div>
  );
}
